import { useEffect, useId, useMemo, useState, type FormEvent } from "react";
import { Link, useLocation, useParams } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { isNotFound, isUnauthorized } from "../../api/errors";
import {
  useDeleteEnvironment,
  useEnvironment,
  useEnvironments,
  useUpdateEnvironment,
} from "../../api/queries";
import type { Environment, EnvironmentClass } from "../../api/types";
import { ErrorState, LoadingState } from "../../components/AsyncStates";
import { Banner } from "../../components/Banner";
import { Breadcrumb } from "../../components/Breadcrumb";
import { Button } from "../../components/Button";
import { EnvironmentBadge } from "../../components/EnvironmentBadge";
import type { UpdateEnvironmentInput } from "../../api/client";
import { useToast } from "../../toast/ToastContext";
import { DeleteEnvironmentDialog } from "./DeleteEnvironmentDialog";
import { ENVIRONMENT_CLASS_OPTIONS, NODE_TYPE_OPTIONS } from "./environmentOptions";
import "../../components/Form.css";
import "./environments.css";

/** Diffs the edited form against the loaded environment, returning only the
 * fields that actually changed (PATCH semantics — the server leaves anything
 * absent unchanged). Node-type sets are compared order-independently. When the
 * `protected` guard is being lowered, `confirmUnprotect` rides along so the
 * server accepts the explicit two-signal unprotect (ADR-0035 BINDING AUDIT
 * CONDITION 6). */
function buildPatch(
  original: Environment,
  form: { name: string; envClass: EnvironmentClass; requiresPromotion: boolean; nodeTypes: string[]; isProtected: boolean },
): UpdateEnvironmentInput {
  const patch: UpdateEnvironmentInput = {};
  if (form.name.trim() !== original.name) patch.name = form.name.trim();
  if (form.envClass !== original.class) patch.class = form.envClass;
  if (form.requiresPromotion !== original.requiresPromotion) patch.requiresPromotion = form.requiresPromotion;
  const sameNodeTypes =
    form.nodeTypes.length === original.allowedNodeTypes.length &&
    form.nodeTypes.every((t) => original.allowedNodeTypes.includes(t));
  if (!sameNodeTypes) patch.allowedNodeTypes = form.nodeTypes;
  if (form.isProtected !== original.protected) {
    patch.protected = form.isProtected;
    // Lowering the guard is the one change the server demands an explicit
    // confirmation for; raising it never needs one.
    if (!form.isProtected) patch.confirmUnprotect = true;
  }
  return patch;
}

export function EnvironmentDetailPage() {
  const { envId } = useParams<{ envId: string }>();
  const { session } = useAuth();
  const { pushToast } = useToast();
  const [, navigate] = useLocation();

  const { data: env, isPending, isError, error, refetch } = useEnvironment(envId);
  // Read-only chain resolution: resolve the source id to a name if it's in the
  // cached list, degrading to the raw id otherwise. A failure here never blocks
  // the page — the detail load above is the page-level query.
  const { data: envList } = useEnvironments();
  const updateMutation = useUpdateEnvironment();
  const deleteMutation = useDeleteEnvironment();

  const nameId = useId();
  const classId = useId();
  const requiresPromotionId = useId();
  const protectedId = useId();

  const [name, setName] = useState("");
  const [envClass, setEnvClass] = useState<EnvironmentClass>("development");
  const [requiresPromotion, setRequiresPromotion] = useState(false);
  const [nodeTypes, setNodeTypes] = useState<string[]>([]);
  const [isProtected, setIsProtected] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Seed the form from the loaded environment; re-seed only when the id changes
  // so a background refetch never clobbers an in-progress edit (matches
  // ServiceDetailPage).
  useEffect(() => {
    if (!env) return;
    setName(env.name);
    setEnvClass(env.class);
    setRequiresPromotion(env.requiresPromotion);
    setNodeTypes(env.allowedNodeTypes);
    setIsProtected(env.protected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env?.id]);

  const sourceName = useMemo(() => {
    if (!env?.sourceEnvId) return null;
    const match = envList?.items.find((e) => e.id === env.sourceEnvId);
    return match?.name ?? env.sourceEnvId;
  }, [env, envList]);

  // environment:update/delete are admin-only (server/internal/authz/model.go).
  // Operator/viewer hold environment:view and see this page read-only. UX only
  // (S-8) — the server enforces the real decision on every request.
  const isAdmin = session?.role === "admin";
  const willUnprotect = Boolean(env?.protected) && !isProtected;

  function toggleNodeType(value: string, checked: boolean) {
    setNodeTypes((prev) => (checked ? [...prev, value] : prev.filter((t) => t !== value)));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!env) return;
    const patch = buildPatch(env, { name, envClass, requiresPromotion, nodeTypes, isProtected });
    if (Object.keys(patch).length === 0) {
      pushToast({ title: "No changes to save.", tone: "info" });
      return;
    }
    updateMutation.mutate(
      { id: env.id, patch },
      {
        onSuccess: () => pushToast({ title: "Environment saved.", tone: "success" }),
        onError: (err) =>
          pushToast({
            title: "Couldn't save environment",
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          }),
      },
    );
  }

  function handleDelete() {
    if (!env) return;
    deleteMutation.mutate(
      { id: env.id },
      {
        onSuccess: () => {
          pushToast({ title: `Deleted "${env.name}".`, tone: "success" });
          setConfirmDeleteOpen(false);
          navigate("/environments");
        },
        onError: (err) => {
          // A protected/in-use refusal (409) is not the operator's mistake —
          // surface the server's own guard message rather than a generic alarm.
          pushToast({
            title: "Couldn't delete environment",
            description: err instanceof Error ? err.message : "Try again in a moment.",
            tone: "error",
          });
          setConfirmDeleteOpen(false);
        },
      },
    );
  }

  if (isPending) {
    return <LoadingState label="Loading environment…" />;
  }

  if (isError) {
    if (isUnauthorized(error)) {
      return <LoadingState label="Loading environment…" />;
    }
    if (isNotFound(error)) {
      return (
        <ErrorState
          title="Environment not found"
          description="It may have been removed, or the link is out of date. Head back to environments."
          action={
            <Link to="/environments" className="button button--secondary">
              Back to Environments
            </Link>
          }
        />
      );
    }
    return (
      <ErrorState
        description={error instanceof Error ? error.message : "Couldn't load this environment."}
        onRetry={refetch}
      />
    );
  }

  if (!env) return null;

  return (
    <div className="environment-detail">
      <Breadcrumb items={[{ label: "Fleet", to: "/hosts" }, { label: "Environments", to: "/environments" }, { label: env.name }]} />

      <div className="environment-detail__header">
        <div className="environment-detail__title-row">
          <h1 className="environment-detail__title">{env.name}</h1>
          <EnvironmentBadge envClass={env.class} />
        </div>
        {isAdmin && (
          <Button variant="danger" onClick={() => setConfirmDeleteOpen(true)}>
            Delete
          </Button>
        )}
      </div>

      <dl className="environment-detail__facts">
        <div>
          <dt>Promotion</dt>
          <dd>{env.requiresPromotion ? "Required (no direct deploys)" : "Direct deploys allowed"}</dd>
        </div>
        <div>
          <dt>Promotes from</dt>
          <dd>
            {env.sourceEnvId ? (
              <Link to={`/environments/${env.sourceEnvId}`}>{sourceName}</Link>
            ) : (
              "No source configured"
            )}
          </dd>
        </div>
        <div>
          <dt>Allowed machine types</dt>
          <dd>{env.allowedNodeTypes.length > 0 ? env.allowedNodeTypes.join(", ") : "No restriction"}</dd>
        </div>
        <div>
          <dt>Protected</dt>
          <dd>{env.protected ? "Yes" : "No"}</dd>
        </div>
      </dl>

      <section className="environment-detail__section" aria-labelledby="environment-configure-heading">
        <h2 id="environment-configure-heading">Configure</h2>
        {!isAdmin && (
          <p className="environment-detail__readonly-note">
            Environments are administered by admins. You can view this environment's policy but not
            change it.
          </p>
        )}
        <form onSubmit={handleSubmit} noValidate>
          <label className="form-field" htmlFor={nameId}>
            <span>Name</span>
            <input
              id={nameId}
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!isAdmin}
              required
              maxLength={63}
            />
          </label>

          <label className="form-field" htmlFor={classId}>
            <span>Class</span>
            <select
              id={classId}
              value={envClass}
              onChange={(e) => setEnvClass(e.target.value as EnvironmentClass)}
              disabled={!isAdmin}
            >
              {ENVIRONMENT_CLASS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {env.protected && (
              <span className="form-field__hint">
                A protected environment's class is frozen. Unprotect it first to re-class it.
              </span>
            )}
          </label>

          <fieldset className="form-field">
            <legend>Allowed machine types</legend>
            {NODE_TYPE_OPTIONS.map((opt) => (
              <label key={opt.value} className="form-field--checkbox__row" htmlFor={`${classId}-nt-${opt.value}`}>
                <input
                  id={`${classId}-nt-${opt.value}`}
                  type="checkbox"
                  checked={nodeTypes.includes(opt.value)}
                  onChange={(e) => toggleNodeType(opt.value, e.target.checked)}
                  disabled={!isAdmin}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </fieldset>

          <label className="form-field form-field--checkbox" htmlFor={requiresPromotionId}>
            <span className="form-field--checkbox__row">
              <input
                id={requiresPromotionId}
                type="checkbox"
                checked={requiresPromotion}
                onChange={(e) => setRequiresPromotion(e.target.checked)}
                disabled={!isAdmin}
              />
              <span>Requires promotion</span>
            </span>
            <span className="form-field__hint">
              When set, this environment refuses direct deploys and accepts only promotions from its
              source environment.
            </span>
          </label>

          <label className="form-field form-field--checkbox" htmlFor={protectedId}>
            <span className="form-field--checkbox__row">
              <input
                id={protectedId}
                type="checkbox"
                checked={isProtected}
                onChange={(e) => setIsProtected(e.target.checked)}
                disabled={!isAdmin}
              />
              <span>Protected</span>
            </span>
            <span className="form-field__hint">
              A protected environment refuses deletion and class changes until it is explicitly
              unprotected.
            </span>
          </label>

          {willUnprotect && (
            <Banner tone="warning" title="Removing protection" className="environment-detail__unprotect-banner">
              Saving will make this environment easier to delete or downgrade. Confirm you meant to turn
              protection off.
            </Banner>
          )}

          {isAdmin && (
            <div className="form-actions">
              <Button type="submit" variant="primary" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Saving…" : "Save changes"}
              </Button>
            </div>
          )}
        </form>
      </section>

      {isAdmin && (
        <DeleteEnvironmentDialog
          environment={confirmDeleteOpen ? env : null}
          isDeleting={deleteMutation.isPending}
          onCancel={() => setConfirmDeleteOpen(false)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}
